import styled from '@emotion/styled'
import { useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { ExportRounded } from '../../../../assets/icons'
import { UPLOAD_COPY } from './uploadCopy'

const DropArea = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  width: 100%;
  min-height: 203px;
  padding: 30px 24px;
  border: 2px dashed ${p => p.theme.donutPlanColor};
  border-radius: 10px;
  background: ${p =>
    p.$active ? (p.theme.primaryLighter ?? '#d4ebf2') : (p.theme.cardHoverBgColor ?? '#f5fafc')};
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover,
  &:focus-visible {
    background: ${p => p.theme.primaryLighter ?? '#d4ebf2'};
  }
`

const HiddenInput = styled.input`
  display: none;
`

/**
 * Dashed drop target that accepts a dragged file or opens the device's file picker.
 *
 * Hand-rolled rather than pulled from a drag-and-drop library: a single-file PDF target needs
 * only the native drag events, and the app has no such dependency today.
 *
 * @param {object} props
 * @param {(files: File[]) => void} props.onFilesSelected
 */
const UploadDropZone = ({ onFilesSelected }) => {
  const inputRef = useRef(null)
  const [dragActive, setDragActive] = useState(false)

  const openPicker = () => inputRef.current?.click()

  const handleChange = event => {
    const files = Array.from(event.target.files ?? [])
    // Reset so re-picking the same file still fires a change event.
    event.target.value = ''
    if (files.length) onFilesSelected(files)
  }

  const handleDragOver = event => {
    event.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = event => {
    event.preventDefault()
    setDragActive(false)
  }

  const handleDrop = event => {
    event.preventDefault()
    setDragActive(false)
    const files = Array.from(event.dataTransfer?.files ?? [])
    if (files.length) onFilesSelected(files)
  }

  return (
    <>
      <DropArea
        type='button'
        $active={dragActive}
        onClick={openPicker}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <ExportRounded />
        {UPLOAD_COPY.dropZone}
      </DropArea>
      <HiddenInput
        ref={inputRef}
        type='file'
        accept='application/pdf,.pdf'
        onChange={handleChange}
        data-testid='statement-file-input'
      />
    </>
  )
}

UploadDropZone.propTypes = {
  onFilesSelected: PropTypes.func.isRequired,
}

export default UploadDropZone
